import { useState, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';

import { Link } from '@comicrelief/component-library';

import RouteProps from '@/types/shared-types';

import GiftAidContainer from './GiftAidContainer';

import { useAppDispatch, useAppSelector } from '@/store/hooks';

import { logger } from '@/services/logger/logger';
import { useJourneyNavigate } from '@/services/journeys/useJourneyNavigate';

import { useValidateTransactionPending } from '@/features/paymentProcess/useValidateTransactionPending';
import { clearErrorMessage } from '@/features/paymentProcess/transactionSlice';
import PaymentErrorText from '@/features/paymentProcess/PaymentErrorText';

import SoftHider from '@/components/softHider/SoftHider';
import Loader from '@/components/loader/Loader';
import StepBack from '@/components/stepBack/StepBack';
import PageHeader from '@/components/pageHeader/PageHeader';

import { initOrderReference, updatePaymentSelectFields } from './paymentSelectSlice';
import { getFormResolver } from './paymentSelectFields';
import ProviderSelectSingle from './ProviderSelectSingle';
import ProviderSelectMonthly from './ProviderSelectMonthly';
import paymentTextContent from './paymentTextContent';

import {
  AsteriskWrapper,
  LegalTextButton
} from './PaymentSelect.style';

const {
  headerText, initialTextSingle, initialTextMonthly, legalText1, legalText2, legalLinkText
} = paymentTextContent;

function PaymentSelect({ providerGroup, totalSteps, currencySymbol }: RouteProps) {
  const dispatch = useAppDispatch();
  const paymentSelectState = useAppSelector(state => state.paymentSelect);
  const urlOverrides = useAppSelector(state => state.urlOverrides);
  const journeyNavigate = useJourneyNavigate();
  const [processing, setProcessing] = useState(false);

  // Validate state is as expected, otherwise redirect to index
  useValidateTransactionPending();

  const formMethods = useForm({
    mode: 'onBlur',
    resolver: getFormResolver(),
  });

  const {
    watch,
    handleSubmit,
  } = formMethods;

  useEffect(() => {
    // Only initiate orderReference if it isn't already set
    if (paymentSelectState?.orderReference) {
      return;
    }

    dispatch(initOrderReference());
  }, [paymentSelectState, dispatch]);

  // Updates the state via the dispatched updatePaymentSelectFields
  // with each form input's value, triggered via RHF's 'watch':
  useEffect(() => {
    const watchInputs = watch((formValues, { name }) => {
      if (!name) return;
      dispatch(updatePaymentSelectFields({
        [name]: !!formValues[name], // Cast giftaid checkbox value to boolean
      }));
    });
    return () => watchInputs.unsubscribe();
  }, [watch, dispatch]);

  const onSubmit = () => {
    if (!paymentSelectState.provider) {
      logger.error('No provider selected');
      return;
    }

    dispatch(clearErrorMessage());
    journeyNavigate('next');
  };

  const [showLegalText, setShowLegalText] = useState(false);

  const handleToggle = () => {
    setShowLegalText(prevLegalContent => !prevLegalContent);
  };

  return (
    <SoftHider show={!processing} loader={<Loader />}>
      <FormProvider {...formMethods}>
        <form onSubmit={event => void handleSubmit(onSubmit)(event)}>

          <PageHeader
            currentStep={2}
            totalSteps={totalSteps ?? 123}
            headerText={headerText}
            initialText={providerGroup === 'monthly' ? initialTextMonthly : initialTextSingle}
            currencySymbol={currencySymbol}
          />

          <PaymentErrorText />

          <GiftAidContainer
            currencySymbol={currencySymbol}
            paymentSelectState={paymentSelectState}
          />

          {!showLegalText
            ? (
              <AsteriskWrapper tag="p" family="Montserrat" size="xs" color="black">
                {legalText1}
                {' '}
                <LegalTextButton onClick={handleToggle}>Read more</LegalTextButton>
              </AsteriskWrapper>
            )
            : (
              <AsteriskWrapper tag="p" family="Montserrat" size="sm" color="black">
                {legalText2}
                {' '}
                <Link href="https://www.comicrelief.com/giftaid/" target="blank">{legalLinkText}</Link>
              </AsteriskWrapper>
            )}

          {(providerGroup === 'single') ? (
            <ProviderSelectSingle
              setProcessing={setProcessing}
            />
          ) : (providerGroup === 'monthly') ? (
            <ProviderSelectMonthly />
          ) : null}

          <StepBack testId="paymentSelectGoBack" isFirstJourneyStep urlOverrides={urlOverrides} />

        </form>
      </FormProvider>
    </SoftHider>
  );
}

export default PaymentSelect;
